import React from 'react';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  RightOutlined,
  TagsOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Badge, Segmented, Spin } from 'antd';
import type { Task, TaskLink, TaskPriority } from '../../api/tasks/interface';
import type {
  CreateTaskInput,
  TasksHookHandler,
} from '../../data/tasks/useTasksHook';
import { collectTags, groupTasks, matchesTags, openBlockers } from './grouping';
import QuickAdd from './QuickAdd';
import { TagFilterBar } from './TaskBits';
import TaskCard from './TaskCard';
import TaskRow from './TaskRow';
import styles from './TasksTab.module.scss';

const LAYOUT_KEY = 'owl-tasks-layout';
type Layout = 'list' | 'cards';

export interface TaskLinkOption {
  type: TaskLink['type'];
  value: string;
  label: string;
}

interface Props {
  tasks: Task[];
  handler: TasksHookHandler;
  loading: boolean;
  assigneeOptions: string[];
  defaultAssignee: string;
  linkOptions: TaskLinkOption[];
  onAddWithDetails: (input: CreateTaskInput) => void;
  onOpenLink?: (link: TaskLink) => void;
  onOpenDetail?: (taskId: string) => void;
}

const TasksPanel: React.FC<Props> = ({
  tasks,
  handler,
  loading,
  assigneeOptions,
  defaultAssignee,
  linkOptions,
  onAddWithDetails,
  onOpenLink,
  onOpenDetail,
}) => {
  const [layout, setLayout] = React.useState<Layout>(
    () => (localStorage.getItem(LAYOUT_KEY) as Layout) || 'list'
  );
  const [showDone, setShowDone] = React.useState(false);
  const [activeTags, setActiveTags] = React.useState<string[]>([]);

  const setLayoutPersist = (value: Layout) => {
    setLayout(value);
    try {
      localStorage.setItem(LAYOUT_KEY, value);
    } catch {
      // ignore storage failures
    }
  };

  // Tags are canonical lowercase (see collectTags), so normalize toggles too.
  const toggleTag = React.useCallback((tag: string) => {
    const t = tag.toLowerCase();
    setActiveTags((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  }, []);
  const clearTags = React.useCallback(() => setActiveTags([]), []);

  const allTags = React.useMemo(() => collectTags(tasks), [tasks]);

  // Only filter by tags that still exist on the dossier's tasks — used for this
  // render so a just-removed tag doesn't briefly show an empty result.
  const effectiveTags = React.useMemo(
    () => activeTags.filter((t) => allTags.some((e) => e.tag === t)),
    [activeTags, allTags]
  );

  // Prune the stored selection to match, so a tag that disappears and later
  // reappears doesn't silently re-activate the filter. This reconciles state
  // with external data, so it must be an effect (not derivable via useMemo);
  // the functional update returns `prev` unchanged when nothing to prune, so
  // React bails out and there's no update loop.
  // eslint-disable-next-line react-etc/prefer-usememo
  React.useEffect(() => {
    setActiveTags((prev) => {
      const present = prev.filter((t) => allTags.some((e) => e.tag === t));
      return present.length === prev.length ? prev : present;
    });
  }, [allTags]);

  const filteredTasks = React.useMemo(
    () => tasks.filter((t) => matchesTags(t, effectiveTags)),
    [tasks, effectiveTags]
  );

  const { groups, done } = React.useMemo(
    () => groupTasks(filteredTasks),
    [filteredTasks]
  );

  // The header badge reflects the dossier's total open tasks, not the filtered
  // subset, so an active tag filter doesn't make the count appear to drop.
  const openCount = React.useMemo(
    () => tasks.filter((t) => t.status !== 'done').length,
    [tasks]
  );

  const taskProps = (task: Task) => ({
    task,
    blockedBy: openBlockers(tasks, task),
    onToggle: () =>
      handler.updateTask(task.id, {
        status: task.status === 'done' ? 'open' : 'done',
      }),
    onChangePriority: (p: TaskPriority) =>
      handler.updateTask(task.id, { priority: p }),
    onSnooze: (due: string) => handler.updateTask(task.id, { due }),
    onOpenLink,
    onOpenDetail,
    activeTags: effectiveTags,
    onTag: toggleTag,
  });

  const renderItems = (items: Task[]) =>
    layout === 'cards' ? (
      <div className={styles.cards}>
        {items.map((t) => (
          <TaskCard key={t.id} {...taskProps(t)} />
        ))}
      </div>
    ) : (
      <div className={styles.rows}>
        {items.map((t) => (
          <TaskRow key={t.id} {...taskProps(t)} />
        ))}
      </div>
    );

  const onAdd = (input: CreateTaskInput) => handler.createTask(input);

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <div className={styles.headTitle}>
          Tasks
          <Badge count={openCount} showZero color="var(--app-color-primary)" />
        </div>
        <Segmented<Layout>
          size="small"
          value={layout}
          onChange={setLayoutPersist}
          options={[
            { value: 'list', icon: <UnorderedListOutlined /> },
            { value: 'cards', icon: <AppstoreOutlined /> },
          ]}
        />
      </div>

      <QuickAdd
        onAdd={onAdd}
        onAddWithDetails={onAddWithDetails}
        assigneeOptions={assigneeOptions}
        defaultAssignee={defaultAssignee}
        linkOptions={linkOptions}
      />

      <TagFilterBar
        tags={allTags}
        active={effectiveTags}
        onToggle={toggleTag}
        onClear={clearTags}
      />

      {loading && tasks.length === 0 ? (
        <div className={styles.empty}>
          <Spin />
        </div>
      ) : groups.length === 0 && done.length === 0 ? (
        effectiveTags.length ? (
          <div className={styles.empty}>
            <TagsOutlined style={{ fontSize: 24 }} />
            <span>
              No tasks tagged {effectiveTags.map((t) => `#${t}`).join(' or ')}.
            </span>
            <button
              type="button"
              className={styles.filterClear}
              onClick={clearTags}
            >
              Clear filter
            </button>
          </div>
        ) : (
          <div className={styles.empty}>
            <CheckCircleOutlined style={{ fontSize: 24 }} />
            <span>No tasks for this policy yet. Add one above.</span>
          </div>
        )
      ) : null}

      {groups.map((g) => (
        <div className={styles.group} key={g.key}>
          <div className={styles.groupHead}>
            <span>{g.label}</span>
            <span>{g.items.length}</span>
          </div>
          {renderItems(g.items)}
        </div>
      ))}

      {done.length ? (
        <div className={styles.group}>
          <button
            type="button"
            className={`${styles.groupHead} ${styles.groupHeadToggle}`}
            onClick={() => setShowDone((s) => !s)}
          >
            <RightOutlined
              className={`${styles.caret} ${showDone ? styles.caretOpen : ''}`}
            />
            <span>Done</span>
            <span>{done.length}</span>
          </button>
          {showDone ? renderItems(done) : null}
        </div>
      ) : null}
    </div>
  );
};

export default TasksPanel;
